/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";

import { BEM, T, useTranslate } from "@adapptio/lib-components";
import { TabsEditor } from "../../../modules/Core/ReferenceGuide/components/TabsEditor/TabEditor";

import "./ReferenceGuideHint.scss";
import { Badge, BADGE_POSITION } from "../../../ui/components/Badge";
import { Markdown } from "../../../ui/components/Markdown";
import { isObject } from "@adapptio/lib-shared";
import { useCommand } from "../../../ui/hooks";
import { TInspectorHintProps } from "../InspectorHintTypes";

export interface TReferenceGuideProps extends TInspectorHintProps {}

export const ReferenceGuideHint: React.FC<TReferenceGuideProps> = ({ node, onClose }) => {
	const baseClass = BEM("edt-reference-hint");

	const [ openRefGuide ] = useCommand("referenceGuide.open");

	const handleClick = useCallback(() => {
		if (node.referenceData?.name) {
			openRefGuide(node.referenceData).then(() => onClose());
		}
	}, [ node ]);

	const t = useTranslate();

	return (
		<div className={baseClass.toClassName()}>
			{node.label && (
				<h2
					className={
						!node.referenceData?.name ? baseClass.element("not-link").toClassName() : undefined
					}
					onClick={handleClick}
				>
					{node.required && <T domain="editor" term={node.label} />}
					{!node.required && (
						<Badge position={BADGE_POSITION.END} content="nullable">
							<T domain="editor" term={node.label} />
						</Badge>
					)}
				</h2>
			)}

			{node.tooltip && <Markdown>{t("editor", node.tooltip)}</Markdown>}

			{!!node.default &&
				(isObject(node.default) ? (
					<div className={baseClass.element("values-default").toClassName()}>
						<div>
							<p>Default value: </p>
						</div>
						<div>
							<ul>
								{Object.keys(node.default).map((item, idx) => (
									<li key={idx}>
										<b>
											{item}:{" "}
											{isObject(node.default[item])
												? node.default[item]?.type
												: node.default[item]}
										</b>
									</li>
								))}
							</ul>
						</div>
					</div>
				) : (
					<div className={baseClass.element("values-default").toClassName()}>
						<p>
							Default value:{" "}
							<b>
								<T domain="editor" term={JSON.stringify(node.default)} />
							</b>
						</p>
					</div>
				))}

			{node.predefineValues?.length && (
				<div className={baseClass.element("values-default").toClassName()}>
					<div>
						<p>Predefined consts: </p>
					</div>
					<div>
						<ul>
							{node.predefineValues.map((item, idx) => (
								<li key={idx}>
									<b>{item}</b>
								</li>
							))}
						</ul>
					</div>
				</div>
			)}

			{node.options && (
				<div className={baseClass.element("values").toClassName()}>
					<div>
						<p>Values:</p>
					</div>
					<div>
						<ul>
							{node.options.map((item, idx) => (
								<li key={idx}>
									<b>
										<T domain="editor" term={item.value as string} />
									</b>
								</li>
							))}
						</ul>
					</div>
				</div>
			)}

			{(node.descriptor || node.examples) && (
				<TabsEditor descriptor={node.descriptor} examples={node.examples} />
			)}
		</div>
	);
};
